import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import queriesApi from '../../api/queries';
import metadataApi from '../../api/metadata';
import {
  EditorView, keymap, lineNumbers, highlightActiveLineGutter
} from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { defaultKeymap, indentWithTab, history, historyKeymap } from '@codemirror/commands';
import { sql } from '@codemirror/lang-sql';
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { lintGutter } from "@codemirror/lint";

const DEFAULT_QUERY = 'select top 100 * \nfrom splus.splus_dr4\nwhere r_auto < 21\nand cone(ra,dec,0.1,0.1,0.05)';

// Define a custom theme for light mode using your CSS variables
const lightEditorTheme = EditorView.theme({
  "&": {
    color: "var(--ui-light-text-primary)",
    backgroundColor: "var(--ui-light-card)"
  },
  ".cm-content": {
    caretColor: "var(--primary-600)"
  },
  "&.cm-focused .cm-cursor": {
    borderLeftColor: "var(--primary-600)"
  },
  ".cm-gutters": {
    backgroundColor: "var(--ui-light-card)",
    color: "var(--ui-light-text-secondary)",
    border: "none"
  }
}, { dark: false });

// Define a custom theme for dark mode using your CSS variables
const darkEditorTheme = EditorView.theme({
  "&": {
    color: "var(--ui-dark-text-primary)",
    backgroundColor: "var(--ui-dark-card)"
  },
  ".cm-content": {
    caretColor: "var(--primary-400)"
  },
  "&.cm-focused .cm-cursor": {
    borderLeftColor: "var(--primary-400)"
  },
  ".cm-gutters": {
    backgroundColor: "var(--ui-dark-card)",
    color: "var(--ui-dark-text-secondary)",
    border: "none"
  }
}, { dark: true });

// Helper to decide which theme to use; here we simply check if the root element has the 'dark' class.
const getEditorTheme = () => {
  const isDark = document.documentElement.classList.contains('dark');
  return isDark ? darkEditorTheme : lightEditorTheme;
};

const QueryEditor = forwardRef(({
  onResultsChange,
  showHistory = false,
  initialQuery = null,
  queryMode: initialQueryMode = 'adql',
  resultFormat: initialResultFormat = 'csv'
}, ref) => {
  const [query, setQuery] = useState(initialQuery || DEFAULT_QUERY);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState('');
  const [executionInfo, setExecutionInfo] = useState(null);
  const [queryMode, setQueryMode] = useState(initialQueryMode);
  const [resultFormat, setResultFormat] = useState(initialResultFormat);
  const [fileUpload, setFileUpload] = useState(null);
  const [tableName, setTableName] = useState('');
  const [isAsyncQuery, setIsAsyncQuery] = useState(false);
  const [asyncJobId, setAsyncJobId] = useState(null);
  const [asyncStatus, setAsyncStatus] = useState(null);
  const [asyncPollInterval, setAsyncPollInterval] = useState(null);
  const [queryHistory, setQueryHistory] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [tables, setTables] = useState({});
  const [isMetadataLoading, setIsMetadataLoading] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  
  const { isAuthenticated } = useAuth();
  const editorRef = useRef(null);
  const editorStateRef = useRef(null);
  const editorViewRef = useRef(null);
  const location = useLocation();
  
  // Setup CodeMirror editor with our custom theme extension
  useEffect(() => {
    if (!editorRef.current) return;
    
    // Grab the correct theme extension based on dark mode
    const themeExtension = getEditorTheme();
    
    const startState = EditorState.create({
      doc: query,
      extensions: [
        lineNumbers(),
        highlightActiveLineGutter(),
        history(),
        EditorView.lineWrapping,
        sql(),
        syntaxHighlighting(defaultHighlightStyle),
        closeBrackets(),
        keymap.of([
          ...defaultKeymap,
          ...historyKeymap,
          ...closeBracketsKeymap,
          indentWithTab
        ]),
        lintGutter(),
        themeExtension, // Apply our custom theme here
        EditorView.theme({
          '&': {
            height: 'auto'
          },
          '.cm-content, .cm-scroller, .cm-editor': {
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace'
          },
          '&.cm-editor .cm-content': {
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace'
          }
        }),
        EditorView.updateListener.of(update => {
          if (update.docChanged) {
            setQuery(update.state.doc.toString());
          }
        })
      ]
    });
    
    const view = new EditorView({
      state: startState,
      parent: editorRef.current
    });
    
    editorStateRef.current = startState;
    editorViewRef.current = view;
    
    return () => {
      if (editorViewRef.current) {
        editorViewRef.current.destroy();
      }
    };
  }, []);
  
  // Update editor content when initialQuery changes or when receiving new query from location state
  useEffect(() => {
    const newQuery = location.state?.query || initialQuery;
    if (newQuery && editorViewRef.current) {
      const transaction = editorViewRef.current.state.update({
        changes: {
          from: 0,
          to: editorViewRef.current.state.doc.length,
          insert: newQuery
        }
      });
      editorViewRef.current.dispatch(transaction);
      setQuery(newQuery);
    }
  }, [location.state, initialQuery]);
  
  // Update local state when props change
  useEffect(() => {
    setQueryMode(initialQueryMode);
  }, [initialQueryMode]);
  
  useEffect(() => {
    setResultFormat(initialResultFormat);
  }, [initialResultFormat]);
  
  // Load metadata for autocomplete
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        setIsMetadataLoading(true);
        const schemasList = await metadataApi.getSchemas();
        setSchemas(schemasList);
        const schemaToLoad = schemasList.includes('public') ? 'public' : schemasList[0];
        if (schemaToLoad) {
          const tablesList = await metadataApi.getTables(schemaToLoad);
          setTables(prevTables => ({ ...prevTables, [schemaToLoad]: tablesList }));
        }
      } catch (error) {
        console.error('Failed to load metadata:', error);
      } finally {
        setIsMetadataLoading(false);
      }
    };
    loadMetadata();
  }, []);
  
  // Load query history
  useEffect(() => {
    if (isAuthenticated && showHistory) {
      queriesApi.getQueryHistory(10).then(setQueryHistory).catch(console.error);
    }
  }, [isAuthenticated, showHistory]);
  
  // Poll async query status using setTimeout instead of setInterval
  useEffect(() => {
    let isMounted = true;
    let shouldPoll = true;
    let timeoutId = null;
    if (!asyncJobId) return;
    console.log(`Setting up status polling for job ${asyncJobId}`);
    const pollStatus = async () => {
      if (!isMounted || !shouldPoll) {
        console.log(`Polling stopped for job ${asyncJobId}`);
        return;
      }
      try {
        console.log(`Fetching status for job ${asyncJobId}`);
        const status = await queriesApi.getQueryStatus(asyncJobId);
        if (!isMounted) return;
        setAsyncStatus(status);
        console.log(`Received status for job ${asyncJobId}:`, status.status);
        const isCompleted = status.status === 'completed' || status.status === 'COMPLETED';
        const isFailed = status.status === 'failed' || status.status === 'ERROR';
        const isCancelled = status.status === 'cancelled' || status.status === 'CANCELLED';
        if (isCompleted || isFailed || isCancelled) {
          console.log(`Job ${asyncJobId} is ${status.status}, stopping polling`);
          shouldPoll = false;
          if (isCompleted) {
            try {
              console.log(`Fetching results for completed job ${asyncJobId}`);
              const results = await queriesApi.getQueryResults(asyncJobId);
              if (!isMounted) return;
              if (typeof onResultsChange === 'function') {
                onResultsChange(results);
              }
              setExecutionInfo({
                rowCount: results.rowCount,
                columnCount: results.columnCount,
                executionTime: status.execution_time_ms,
                status: 'completed'
              });
            } catch (err) {
              if (!isMounted) return;
              console.error('Error fetching results:', err);
              setError(`Query completed but results couldn't be loaded: ${err.message}`);
            }
          } else if (isCancelled) {
            setError(`Query was cancelled: ${status.error || 'Query cancelled by user'}`);
            if (typeof onResultsChange === 'function') {
              onResultsChange(null);
            }
          } else {
            setError(`Query failed: ${status.error || 'Unknown error'}`);
            if (typeof onResultsChange === 'function') {
              onResultsChange(null);
            }
          }
          setIsExecuting(false);
        } else {
          console.log(`Job ${asyncJobId} is ${status.status}, continuing to poll`);
          timeoutId = setTimeout(pollStatus, 2000);
        }
      } catch (error) {
        if (!isMounted) return;
        console.error(`Error polling status for job ${asyncJobId}:`, error);
        setError(`Failed to check query status: ${error.message}`);
        setIsExecuting(false);
        shouldPoll = false;
      }
    };
    pollStatus();
    return () => {
      console.log(`Cleaning up polling for job ${asyncJobId}`);
      isMounted = false;
      shouldPoll = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [asyncJobId]);
  
  const executeQuery = async () => {
    if (!query.trim()) {
      setError('Please enter a query');
      return;
    }
    setIsExecuting(true);
    setError('');
    setAsyncJobId(null);
    setAsyncStatus(null);
    setAsyncPollInterval(null);
    setExecutionInfo(null);
    if (typeof onResultsChange === 'function') {
      console.log('Clearing previous results before starting new query');
      onResultsChange(null);
    }
    try {
      if (isAsyncQuery) {
        console.log('Executing as async query');
        const result = await queriesApi.executeAsync(query, queryMode, fileUpload, tableName, resultFormat);
        console.log('Async query submitted:', result);
        if (result && result.id) {
          setAsyncJobId(result.id);
          setExecutionInfo({
            status: 'submitted',
            jobId: result.id,
            submittedAt: new Date().toISOString(),
            isAsync: true,
            format: resultFormat
          });
          try {
            const statusResponse = await queriesApi.getQueryStatus(result.id);
            setAsyncStatus(statusResponse);
          } catch (statusError) {
            console.error('Failed to get initial status:', statusError);
          }
        } else {
          throw new Error('Invalid response from server: missing job ID');
        }
      } else {
        console.log('Executing as sync query');
        const result = await queriesApi.executeSync(query, queryMode, fileUpload, tableName, resultFormat);
        if (result.isAsync && result.id) {
          console.log('Server redirected sync query to async:', result);
          setAsyncJobId(result.id);
          setExecutionInfo({
            status: 'submitted',
            jobId: result.id,
            submittedAt: new Date().toISOString(),
            isAsync: true,
            message: result.message
          });
          try {
            const statusResponse = await queriesApi.getQueryStatus(result.id);
            setAsyncStatus(statusResponse);
          } catch (statusError) {
            console.error('Failed to get initial status:', statusError);
          }
        } else {
          console.log('Sync query completed with results');
          onResultsChange(result);
          setExecutionInfo({
            rowCount: result.rowCount,
            columnCount: result.columnCount,
            executionTime: result.executionTime,
            isAsync: false
          });
          setIsExecuting(false);
        }
      }
    } catch (error) {
      console.error('Query execution error:', error);

      // fallback payload
      let payload = error.response?.data;
      // if it’s an ArrayBuffer, decode & parse it
      if (payload instanceof ArrayBuffer) {
        try {
          const text = new TextDecoder('utf-8').decode(payload);
          payload = JSON.parse(text);
        } catch (e) {
          console.error('Failed to decode error buffer:', e);
        }
      }
      var errorMessage = '';
      if (payload && payload.message) {
        errorMessage = payload.message;
      } else {
        console.log('Error response:', error.response);
        
        // Try to handle the case where error.response.data is a string containing JSON
        if (typeof error.response?.data === 'string') {
          try {
            // Attempt to parse the string as JSON
            const parsedData = JSON.parse(error.response.data);
            errorMessage = parsedData.message || parsedData.error || 'Unknown error';
          } catch (jsonError) {
            // If parsing fails, use the string directly
            errorMessage = error.response.data;
          }
        } else {
          // Handle regular error objects
          errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
        }
}
      console.log('Error message:', errorMessage);
      setError(errorMessage);

      onResultsChange(null);
      setIsExecuting(false);
      if (typeof errorMessage === 'string' && errorMessage.includes('job_id')) {
        try {
          const match = errorMessage.match(/job_id[":=\s]+([a-f0-9-]+)/i);
          if (match && match[1]) {
            const jobId = match[1];
            console.log('Found job ID in error response:', jobId);
            setAsyncJobId(jobId);
            setExecutionInfo({
              status: 'submitted',
              jobId: jobId,
              submittedAt: new Date().toISOString(),
              isAsync: true
            });
            try {
              const statusResponse = await queriesApi.getQueryStatus(jobId);
              setAsyncStatus(statusResponse);
            } catch (statusError) {
              console.error('Failed to get initial status:', statusError);
            }
          }
        } catch (parseError) {
          console.error('Failed to extract job ID from error:', parseError);
        }
      }
    }
  };
  
  // Expose methods to parent component through ref
  useImperativeHandle(ref, () => ({
    insertTextAtCursor: (text) => {
      if (!editorViewRef.current) return;
      const view = editorViewRef.current;
      const { state } = view;
      const { selection } = state;
      const insertPos = selection.main.from;
      const transaction = state.update({
        changes: {
          from: selection.main.from,
          to: selection.main.to,
          insert: text
        },
        selection: {
          anchor: insertPos + text.length,
          head: insertPos + text.length
        }
      });
      view.dispatch(transaction);
      view.focus();
    },
    setContent: (content) => {
      if (!editorViewRef.current) return;
      const view = editorViewRef.current;
      const transaction = view.state.update({
        changes: {
          from: 0,
          to: view.state.doc.length,
          insert: content
        },
        selection: {
          anchor: content.length,
          head: content.length
        }
      });
      view.dispatch(transaction);
      view.focus();
    }
  }));

  // Load a previous query from history
const loadQueryFromHistory = (queryText) => {
  if (editorViewRef.current) {
    const transaction = editorViewRef.current.state.update({
      changes: {
        from: 0,
        to: editorViewRef.current.state.doc.length,
        insert: queryText,
      },
    });
    editorViewRef.current.dispatch(transaction);
    setQuery(queryText);
  }
};
  
  return (
    <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Query Editor</h2>
        {showHistory && (
          <button
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="px-3 py-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 border border-primary-200 dark:border-primary-800 rounded-md transition-colors"
          >
            {historyExpanded ? 'Hide History' : 'Show History'}
          </button>
        )}
      </div>
      
      {historyExpanded && queryHistory.length > 0 && (
        <div className="mb-4 border border-ui-light-border dark:border-ui-dark-border rounded-md">
          <div className="p-2 bg-ui-light-hover dark:bg-ui-dark-hover border-b border-ui-light-border dark:border-ui-dark-border">
            <h3 className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Recent Queries</h3>
          </div>
          <ul className="divide-y divide-ui-light-border dark:divide-ui-dark-border">
            {queryHistory.slice(0, 5).map((historyItem) => (
              <li key={historyItem.id} className="p-2 hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                <button onClick={() => loadQueryFromHistory(historyItem.query_text)} className="w-full text-left">
                  <div className="text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary truncate">
                    {historyItem.query_text}
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                      {new Date(historyItem.created_at).toLocaleString()}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                      historyItem.status === 'completed'
                        ? 'text-status-success-light-text bg-status-success-light-bg dark:text-status-success-dark-text dark:bg-status-success-dark-bg'
                        : historyItem.status === 'failed'
                        ? 'text-status-error-light-text bg-status-error-light-bg dark:text-status-error-dark-text dark:bg-status-error-dark-bg'
                        : 'text-status-warning-light-text bg-status-warning-light-bg dark:text-status-warning-dark-text dark:bg-status-warning-dark-bg'
                    }`}>
                      {historyItem.status}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="border border-ui-light-border dark:border-ui-dark-border rounded-md overflow-hidden mb-3">
        <div ref={editorRef} className="min-h-32 max-h-96 resize-y overflow-auto"></div>
      </div>
      
      <div className="flex flex-col space-y-3 mb-3">
        <div className="flex items-center space-x-3">
          <label className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Upload Table:</label>
          <input
            type="file"
            accept=".csv,.parquet,.fits"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setFileUpload(e.target.files[0]);
              }
            }}
            className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-100 file:text-primary-700 dark:file:bg-primary-900/30 dark:file:text-primary-400 hover:file:bg-primary-200 dark:hover:file:bg-primary-800/50"
          />
          <input
            type="text"
            placeholder="Table name (alphanumeric)"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            className="px-3 py-1 text-sm border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card"
          />
          {fileUpload && (
            <button
              onClick={() => {
                setFileUpload(null);
                setTableName('');
              }}
              className="p-1 text-status-error-light-text dark:text-status-error-dark-text hover:bg-status-error-light-bg/20 dark:hover:bg-status-error-dark-bg/20 rounded-full"
              title="Remove file"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
        
        {fileUpload && (
          <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary ml-[100px]">
            After uploading, you can reference this table as <code className="bg-ui-light-hover dark:bg-ui-dark-hover px-1 rounded">upload.{tableName}</code> in your query.
          </p>
        )}
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={executeQuery}
            disabled={isExecuting}
            className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
          >
            {isExecuting && !asyncJobId ? 'Executing...' : 'Execute Query'}
          </button>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="asyncQuery"
              checked={isAsyncQuery}
              onChange={() => setIsAsyncQuery(!isAsyncQuery)}
              className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-ui-light-border dark:border-ui-dark-border rounded"
            />
            <label htmlFor="asyncQuery" className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
              Run as async query
            </label>
          </div>
        </div>
        
        {executionInfo && (
          <div className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            {executionInfo.status === 'submitted' && asyncJobId && (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-primary-600 dark:text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Async query running (Job ID: {asyncJobId})</span>
                {asyncStatus && (
                  <span className="ml-2">
                    Status: {asyncStatus.status}
                    {asyncStatus.progress && ` (${asyncStatus.progress}%)`}
                  </span>
                )}
              </div>
            )}
            {executionInfo.rowCount !== undefined && (
              <span className="mr-3">{executionInfo.rowCount.toLocaleString()} rows</span>
            )}
            {executionInfo.executionTime !== undefined && (
              <span>Executed in {executionInfo.executionTime}ms</span>
            )}
          </div>
        )}
      </div>
      
      {asyncJobId && !error && (
        <div className="mt-3 p-3 text-sm bg-primary-50 dark:bg-primary-900/30 rounded-md border border-primary-100 dark:border-primary-800/50">
          <div className="flex justify-between items-center">
            <p className="font-medium text-primary-700 dark:text-primary-400">
              {(asyncStatus?.status === 'completed' || asyncStatus?.status === 'COMPLETED')
                ? 'Async Query Completed:'
                : (asyncStatus?.status === 'failed' || asyncStatus?.status === 'ERROR' || asyncStatus?.status === 'cancelled')
                ? 'Async Query Failed:'
                : 'Async Query Status:'}
            </p>
            
            {asyncStatus && 
              !['completed', 'COMPLETED', 'failed', 'ERROR', 'cancelled'].includes(asyncStatus.status) && (
              <button 
                onClick={async () => {
                  try {
                    await queriesApi.cancelQuery(asyncJobId);
                    setError('Query cancelled by user');
                    setIsExecuting(false);
                    if (asyncPollInterval) {
                      clearInterval(asyncPollInterval);
                      setAsyncPollInterval(null);
                    }
                  } catch (err) {
                    console.error('Failed to cancel query:', err);
                    setError(`Failed to cancel query: ${err.message}`);
                  }
                }}
                className="px-2 py-1 text-xs bg-status-error-light-bg dark:bg-status-error-dark-bg text-status-error-light-text dark:text-status-error-dark-text rounded hover:bg-status-error-light-bg/70 dark:hover:bg-status-error-dark-bg/70"
              >
                Cancel Query
              </button>
            )}
            
            {asyncStatus && 
              (asyncStatus.status === 'completed' || asyncStatus.status === 'COMPLETED') && (
              <a
                href="/queries/history"
                className="px-2 py-1 text-xs bg-status-success-light-bg dark:bg-status-success-dark-bg text-status-success-light-text dark:text-status-success-dark-text rounded hover:bg-status-success-light-bg/70 dark:hover:bg-status-success-dark-bg/70"
              >
                View in History
              </a>
            )}
          </div>
          
          <div className="mt-1">
            <p><strong>Job ID:</strong> {asyncJobId}</p>
            <p>
              <strong>Status:</strong> 
              <span className={`ml-1 ${
                ['completed', 'COMPLETED'].includes(asyncStatus?.status) ? 'text-status-success-light-text dark:text-status-success-dark-text' : 
                ['failed', 'ERROR'].includes(asyncStatus?.status) ? 'text-status-error-light-text dark:text-status-error-dark-text' : 
                ['running', 'RUNNING'].includes(asyncStatus?.status) ? 'text-primary-600 dark:text-primary-400' : 
                'text-status-warning-light-text dark:text-status-warning-dark-text'
              }`}>
                {asyncStatus?.status || 'Pending'}
              </span>
              
              {['running', 'RUNNING'].includes(asyncStatus?.status) && (
                <svg className="inline-block animate-spin ml-2 h-3 w-3 text-primary-600 dark:text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </p>
            
            {(['running', 'RUNNING'].includes(asyncStatus?.status)) && asyncStatus?.progress && (
              <div className="mt-1">
                <p><strong>Progress:</strong> {asyncStatus.progress}%</p>
                <div className="w-full bg-ui-light-hover dark:bg-ui-dark-hover rounded-full h-2.5 mt-1">
                  <div 
                    className="bg-primary-600 dark:bg-primary-700 h-2.5 rounded-full" 
                    style={{ width: `${asyncStatus.progress}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            {asyncStatus?.queue_position && (
              <p><strong>Queue Position:</strong> {asyncStatus.queue_position}</p>
            )}
            
            {asyncStatus?.submitted_at && (
              <p><strong>Submitted:</strong> {new Date(asyncStatus.submitted_at).toLocaleString()}</p>
            )}
            
            {asyncStatus?.error && (
              <div className="mt-2 p-2 bg-status-error-light-bg dark:bg-status-error-dark-bg text-status-error-light-text dark:text-status-error-dark-text rounded">
                <p><strong>Error:</strong> {asyncStatus.error}</p>
              </div>
            )}
            
            {executionInfo?.message && (
              <div className="mt-2 p-2 bg-status-warning-light-bg dark:bg-status-warning-dark-bg text-status-warning-light-text dark:text-status-warning-dark-text rounded">
                <p>{executionInfo.message}</p>
              </div>
            )}
            
            {(asyncStatus?.status === 'completed' || asyncStatus?.status === 'COMPLETED') && 
              executionInfo?.rowCount !== undefined && asyncStatus?.format === 'CSV' && (
              <div className="mt-2 p-2 bg-status-success-light-bg dark:bg-status-success-dark-bg text-status-success-light-text dark:text-status-success-dark-text rounded">
                <p>
                  <strong>Results:</strong> {executionInfo.rowCount.toLocaleString()} rows
                  {executionInfo.executionTime && ` in ${executionInfo.executionTime}ms`}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {error && (
        <div className="mt-3 p-3 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md border border-status-error-light-text/20 dark:border-status-error-dark-text/20">
          <p className="font-medium">Error:</p>
          <p className="whitespace-pre-wrap">{error}</p>
        </div>
      )}
    </div>
  );
});

export default QueryEditor;